import { useContext, type CSSProperties } from "react"
import { useTranslation } from "react-i18next"
import Header from "../../../components/header"
import { ProjectContext } from "../../../App"
import { Navigate, useNavigate } from "react-router"
import {
  getReadablePrimaryButtonStyle,
  resolveOptionalColor,
  resolveThemeColor,
} from "../../../utils/buttonColor"

export default function AboutUsSection() {
  const { project } = useContext(ProjectContext)
  const navigate = useNavigate()
  const { t } = useTranslation('home')

  if (!project) return null

  if (!project.aboutUsEnabled) {
    return <Navigate to="/" replace />
  }

  const themeColor = resolveThemeColor(project.themeColor)
  const buttonColor = resolveThemeColor(project.buttonColor)
  const serviceColor = resolveOptionalColor(project.serviceColor)
  const coverUrl = project.coverImageFiles?.[0]?.s3Url
  const aboutUsContent = project.aboutUsContent ?? []

  const backgroundStyles: CSSProperties = coverUrl
    ? {
        backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.22) 0%, rgba(15, 23, 42, 0.4) 100%), linear-gradient(135deg, ${themeColor}18 0%, transparent 50%), url(${JSON.stringify(coverUrl)})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : {
        background: `linear-gradient(160deg, ${themeColor} 0%, ${themeColor}dd 45%, ${themeColor}aa 100%)`,
      }

  return (
    <section
      className="relative flex min-h-screen flex-col"
      style={backgroundStyles}
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/12 to-transparent md:from-black/18"
        aria-hidden
      />

      <Header project={project} />

      <div className="relative z-[1] flex flex-1 flex-col px-5 pb-12 pt-8 md:pb-16 md:pt-10">
        <header className="mx-auto mb-8 max-w-3xl text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-white drop-shadow-sm md:text-4xl">
            About us
          </h1>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-white/90">
            {project.name}
          </p>
        </header>

        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 md:gap-8">
          {aboutUsContent.length === 0 ? (
            <div className="rounded-2xl border border-white/20 bg-white/95 px-6 py-10 text-center text-sm text-gray-600 shadow-xl shadow-black/10 backdrop-blur-sm">
              No about content has been configured yet.
            </div>
          ) : (
            aboutUsContent.map((aboutUs, index) => (
              <article
                key={index}
                className="overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-xl shadow-gray-900/10"
              >
                <div
                  className={`flex flex-col md:flex-row ${index % 2 === 1 ? "md:flex-row-reverse" : ""}`}
                >
                  <div className="relative overflow-hidden md:w-1/2">
                    <img
                      src={aboutUs.imageUrl}
                      alt={aboutUs.title}
                      className="h-full min-h-[220px] w-full object-cover sm:min-h-[260px]"
                    />
                  </div>
                  <div
                    className="flex flex-col justify-center p-6 sm:p-8 md:w-1/2 md:min-h-[260px]"
                    style={{
                      borderLeftWidth: 4,
                      borderLeftColor: themeColor,
                      ...(serviceColor
                        ? {
                            background: `linear-gradient(145deg, ${serviceColor}0c 0%, #ffffff 48%)`,
                          }
                        : { backgroundColor: "#ffffff" }),
                    }}
                  >
                    <h2
                      className="text-xl font-semibold leading-snug tracking-tight sm:text-2xl"
                      style={{ color: themeColor }}
                    >
                      {aboutUs.title}
                    </h2>
                    <div
                      className="mt-3 h-px w-14"
                      style={{ backgroundColor: themeColor, opacity: 0.35 }}
                      aria-hidden
                    />
                    <div
                      className="mt-5 max-w-none text-left text-sm leading-relaxed text-gray-700 [&_a]:font-medium [&_a]:underline [&_a]:decoration-gray-400"
                      dangerouslySetInnerHTML={{ __html: aboutUs.content }}
                    />
                  </div>
                </div>
              </article>
            ))
          )}

          <div className="mt-2 flex flex-col items-center gap-3 rounded-2xl border border-white/20 bg-white/95 px-6 py-8 text-center shadow-xl shadow-black/10 backdrop-blur-sm sm:px-10">
            <p className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: themeColor }}>
              {t('readyToBook')}
            </p>
            <h3 className="text-lg font-semibold tracking-tight text-gray-900 sm:text-xl">
              {t('aboutCtaSubtitle')}
            </h3>
            <button
              type="button"
              onClick={() => navigate("/#services")}
              className="mt-2 w-full rounded-xl px-8 py-3.5 text-sm font-semibold shadow-md transition hover:brightness-105 active:brightness-95 sm:w-auto"
              style={getReadablePrimaryButtonStyle(buttonColor)}
            >
              {t('bookNow')}
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
